import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { exportRowsToCSV } from "@/lib/csvExport";
import { Download, Loader2, Wallet, Undo2, AlertTriangle } from "lucide-react";

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  converted: "bg-blue-100 text-blue-800",
  applied: "bg-green-100 text-green-800",
  unknown: "bg-gray-100 text-gray-700",
};

const AdvancePaymentsPanel = () => {
  const [data, setData] = useState({ rows: [], total_collected: 0, total_pending: 0, total_converted: 0, total_applied: 0 });
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ customer_id: "all", start_date: "", end_date: "" });
  const [reverseTarget, setReverseTarget] = useState(null);
  const [reversing, setReversing] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.customer_id !== "all") params.customer_id = filters.customer_id;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      const res = await axios.get(`${API}/accounting/advance-payments`, { params });
      setData(res.data);
    } catch (error) {
      toast.error("Failed to load advance payments");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    axios.get(`${API}/customers`).then((res) => setCustomers(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatMethod = (m) => (m || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const vehicleLabel = (r) => {
    if (r.vehicle_make || r.vehicle_model) return [r.vehicle_make, r.vehicle_model].filter(Boolean).join(' ');
    return r.vehicle_info || 'N/A';
  };

  const reversalAmountLabel = (r) => {
    if (!r) return '';
    const total = r.reversal_total;
    if (!total) return `₹${(r.amount || 0).toLocaleString('en-IN')}`;
    const parts = [];
    if (total.cash) parts.push(`₹${total.cash.toLocaleString('en-IN')} cash`);
    if (total.bank) parts.push(`₹${total.bank.toLocaleString('en-IN')} bank`);
    if (!parts.length) return 'no cash/bank balance (UPI/card advance)';
    return parts.join(' + ');
  };

  const handleReverse = async () => {
    if (!reverseTarget) return;
    setReversing(true);
    try {
      const res = await axios.post(`${API}/quotations/${reverseTarget.quotation_id}/reverse-advance`);
      const cash = res.data?.cash || 0;
      const bankTotal = Object.values(res.data?.bank || {}).reduce((s, v) => s + v, 0);
      const parts = [];
      if (cash) parts.push(`₹${cash.toLocaleString('en-IN')} cash`);
      if (bankTotal) parts.push(`₹${bankTotal.toLocaleString('en-IN')} bank`);
      toast.success(`Advance reversed${parts.length ? ` — ${parts.join(' + ')} debited` : ''}`);
      setReverseTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reverse advance");
    } finally {
      setReversing(false);
    }
  };

  const handleExport = () => {
    exportRowsToCSV(
      "advance_payments",
      ["Date", "Customer", "Vehicle", "Document #", "Amount", "Payment Method", "Status"],
      data.rows.map((r) => [
        new Date(r.date).toLocaleDateString('en-IN'),
        r.customer_name || '',
        vehicleLabel(r),
        r.document_number || '',
        r.amount,
        formatMethod(r.payment_method),
        r.status_label,
      ])
    );
  };

  return (
    <div className="space-y-6 mt-6" data-testid="advance-payments-panel">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white">
          <CardContent className="p-5">
            <p className="text-sm opacity-90">Total Collected</p>
            <p className="text-2xl font-bold" data-testid="advance-total-collected">₹{data.total_collected.toLocaleString('en-IN')}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-amber-500 to-amber-600 text-white">
          <CardContent className="p-5">
            <p className="text-sm opacity-90">Pending (Not Invoiced)</p>
            <p className="text-2xl font-bold" data-testid="advance-total-pending">₹{data.total_pending.toLocaleString('en-IN')}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <CardContent className="p-5">
            <p className="text-sm opacity-90">Converted to Invoice</p>
            <p className="text-2xl font-bold" data-testid="advance-total-converted">₹{data.total_converted.toLocaleString('en-IN')}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
          <CardContent className="p-5">
            <p className="text-sm opacity-90">Applied to Invoice</p>
            <p className="text-2xl font-bold" data-testid="advance-total-applied">₹{data.total_applied.toLocaleString('en-IN')}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Wallet className="h-5 w-5" /> Advance Payments ({data.count || data.rows.length})
            </CardTitle>
            <Button variant="outline" size="sm" onClick={handleExport} disabled={data.rows.length === 0} data-testid="advance-payments-export-btn">
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
          </div>
          <div className="flex flex-wrap gap-3 mt-3">
            <div>
              <Label className="text-xs text-gray-500">Customer</Label>
              <Select value={filters.customer_id} onValueChange={(v) => setFilters({ ...filters, customer_id: v })}>
                <SelectTrigger className="w-[200px]" data-testid="advance-filter-customer"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Customers</SelectItem>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">From</Label>
              <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} data-testid="advance-filter-start-date" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">To</Label>
              <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} data-testid="advance-filter-end-date" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
          ) : data.rows.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8" data-testid="advance-payments-empty">No advance payments found for the selected filters.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="advance-payments-table">
                <thead>
                  <tr className="border-b text-left text-gray-500">
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4">Customer</th>
                    <th className="py-2 pr-4">Vehicle</th>
                    <th className="py-2 pr-4">Document #</th>
                    <th className="py-2 pr-4">Method</th>
                    <th className="py-2 pr-4 text-right">Amount</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const shownQuotations = new Set();
                    return data.rows.map((r) => {
                      const showBtn = r.needs_reversal && r.quotation_id && !shownQuotations.has(r.quotation_id);
                      if (showBtn) shownQuotations.add(r.quotation_id);
                      return (
                    <tr key={r.id} className="border-b last:border-0" data-testid={`advance-payment-row-${r.id}`}>
                      <td className="py-2 pr-4 whitespace-nowrap">{new Date(r.date).toLocaleDateString('en-IN')}</td>
                      <td className="py-2 pr-4">{r.customer_name || 'N/A'}</td>
                      <td className="py-2 pr-4" data-testid={`advance-payment-vehicle-${r.id}`}>{vehicleLabel(r)}</td>
                      <td className="py-2 pr-4">{r.document_number || 'N/A'}</td>
                      <td className="py-2 pr-4 capitalize">{(r.payment_method || '').replace('_', ' ')}</td>
                      <td className="py-2 pr-4 text-right font-medium">₹{r.amount.toLocaleString('en-IN')}</td>
                      <td className="py-2 pr-4">
                        <Badge className={STATUS_STYLES[r.status] || STATUS_STYLES.unknown}>{r.status_label}</Badge>
                      </td>
                      <td className="py-2 pr-4">
                        {showBtn && (
                          r.reversal_blocked_reason ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-gray-400 border-gray-200 cursor-not-allowed"
                              disabled
                              title={r.reversal_blocked_reason}
                              data-testid={`advance-reverse-btn-${r.id}`}
                            >
                              <Undo2 className="h-3.5 w-3.5 mr-1.5" /> Reverse
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-300 hover:bg-red-50"
                              onClick={() => setReverseTarget(r)}
                              data-testid={`advance-reverse-btn-${r.id}`}
                            >
                              <Undo2 className="h-3.5 w-3.5 mr-1.5" /> Reverse
                            </Button>
                          )
                        )}
                      </td>
                    </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!reverseTarget} onOpenChange={(o) => !o && setReverseTarget(null)}>
        <DialogContent className="max-w-md" data-testid="advance-reverse-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" /> Reverse Advance Payment?
            </DialogTitle>
            <DialogDescription>
              This quotation ({reverseTarget?.document_number || 'N/A'}) was deleted before advance
              reversal was automatic. Confirming will debit{' '}
              <span className="font-semibold">{reversalAmountLabel(reverseTarget)}</span>{' '}
              and mark this advance as reversed. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReverseTarget(null)} disabled={reversing} data-testid="advance-reverse-cancel-btn">
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleReverse}
              disabled={reversing}
              data-testid="advance-reverse-confirm-btn"
            >
              {reversing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Undo2 className="h-4 w-4 mr-2" />}
              Reverse Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdvancePaymentsPanel;
